import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, PanelLeftOpen, PanelLeftClose, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';

// Custom hook for notifications (without polling)
const useNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchNotifications = async () => {
        try {
           // console.log('📢 Fetching notifications from:', 'https://n8n.acespireconsulting.com/webhook/notifications');

            // Get notifications from your n8n webhook
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/notifications');

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
           // console.log('📢 Notifications response:', data);

            // Handle both array and object responses
            const notificationArray = Array.isArray(data) ? data : data.data || [];

            setNotifications(notificationArray);
            setLoading(false);

            //console.log(`📢 Loaded ${notificationArray.length} notifications`);

        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            // Set empty array on error to prevent continuous loading
            setNotifications([]);
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initial fetch only - no polling
        fetchNotifications();
    }, []); // Empty dependency array - only run once on mount

    // Return the fetch function so it can be called manually if needed
    return { notifications, loading, refetchNotifications: fetchNotifications };
};

const Sidebar = ({ activeView = 'bulk', onViewChange }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const { notifications, loading, refetchNotifications } = useNotifications();
    const navigate = useNavigate();

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const toggleNotificationDropdown = () => {
        setIsNotificationOpen(!isNotificationOpen);
    };

    const handleNotificationClick = (notification) => {
       // console.log('📢 Notification clicked:', notification.candidate_name);
        // Navigate to candidate profile
        navigate(`${notification.candidate_profile_url}/dashboard`);
        // Close dropdown after navigation
        setIsNotificationOpen(false);
    };

    return (
        <div className={`${isCollapsed ? 'w-16' : 'w-64'} flex-shrink-0 border-r bg-[#EAEFF5] border-gray-200 flex flex-col min-h-screen transition-all duration-300 ease-in-out`}>
            {/* Logo */}
            <div className="bg-[#275F97] text-white p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <img
                        src="/assets/hireon-logo.png"
                        alt="Hireon AI Logo"
                        className={`${isCollapsed ? 'w-8 h-8' : 'w-16 h-16'} object-contain transition-all duration-300`}
                    />
                    {!isCollapsed && (
                        <span className="text-xl font-semibold italic opacity-100 transition-opacity duration-300">
                            Hireon AI
                        </span>
                    )}
                </div>

                {/* Toggle Button */}
                <button
                    onClick={toggleSidebar}
                    className="rounded-lg w-8 h-8 flex items-center justify-center transition-colors"
                >
                    {isCollapsed ? (
                        <PanelLeftOpen className="w-6 h-6 text-white" />
                    ) : (
                        <PanelLeftClose className="w-6 h-6 text-white" />
                    )}
                </button>
            </div>

            {/* NEW: Candidates Navigation Section */}
            {onViewChange && (
                <div className="bg-[#EAEFF5] p-5 border-b border-gray-200">
                    {!isCollapsed && (
                        <h3 className="text-md text-gray-500 uppercase tracking-wide mb-4 font-medium">
                            Candidates
                        </h3>
                    )}

                    <div className="space-y-2">
                        <button
                            onClick={() => onViewChange('bulk')}
                            className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                                activeView === 'bulk' 
                                    ? 'bg-[#275F97] text-white' 
                                    : 'hover:bg-gray-100'
                            }`}
                        >
                            {isCollapsed ? (
                                <div className={`w-2 h-2 rounded-full mx-auto ${
                                    activeView === 'bulk' ? 'bg-white' : 'bg-gray-500'
                                }`}></div>
                            ) : (
                                <>
                                    <span className="text-sm font-medium">Admin Uploads</span>
                                    {activeView === 'bulk' && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                </>
                            )}
                        </button>

                        <button
                            onClick={() => onViewChange('public')}
                            className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                                activeView === 'public' 
                                    ? 'bg-[#275F97] text-white' 
                                    : 'hover:bg-gray-100'
                            }`}
                        >
                            {isCollapsed ? (
                                <div className={`w-2 h-2 rounded-full mx-auto ${
                                    activeView === 'public' ? 'bg-white' : 'bg-gray-500'
                                }`}></div>
                            ) : (
                                <>
                                    <span className="text-sm font-medium">Candidate Applications</span>
                                    {activeView === 'public' && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* More AI Section */}
            <div className="bg-[#EAEFF5] p-5 border-b border-gray-200">
                {!isCollapsed && (
                    <h3 className="text-md text-gray-500 uppercase tracking-wide mb-4 font-medium">
                        More AI
                    </h3>
                )}

                <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                        {isCollapsed ? (
                            <div className="w-2 h-2 bg-green-500 rounded-full mx-auto"></div>
                        ) : (
                            <span className="text-sm">DigiMark AI</span>
                        )}
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                        {isCollapsed ? (
                            <div className="w-2 h-2 bg-green-500 rounded-full mx-auto"></div>
                        ) : (
                            <span className="text-sm">ScriptAI AI</span>
                        )}
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-[#275F97] text-white">
                        {isCollapsed ? (
                            <div className="w-2 h-2 bg-white rounded-full mx-auto"></div>
                        ) : (
                            <>
                                <span className="text-sm font-medium">Hireon AI</span>
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Clean Notifications Section with Dropdown */}
            <div className="bg-[#EAEFF5] p-5 border-b border-gray-200 flex-1">
                {isCollapsed ? (
                    // Collapsed view - show simple dot
                    <div className="flex justify-center">
                        <div className="w-2 h-2 bg-[#275F97] rounded-full"></div>
                    </div>
                ) : (
                    // Expanded view with dropdown
                    <div>
                        {/* Notification Header with Dropdown Toggle */}
                        <button
                            onClick={toggleNotificationDropdown}
                            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <h3 className="text-md text-black uppercase tracking-wide font-medium">
                                    Notifications
                                </h3>
                            </div>
                            {isNotificationOpen ? (
                                <ChevronUp className="w-4 h-4 text-gray-500" />
                            ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                        </button>

                        {/* Dropdown Content */}
                        {isNotificationOpen && (
                            <div className="mt-3 space-y-2 max-h-80 overflow-y-auto transition-all duration-300 ease-in-out animate-in slide-in-from-top-2 fade-in">
                                {loading ? (
                                    <div className="text-sm text-gray-500 p-3">Loading notifications...</div>
                                ) : notifications.length === 0 ? (
                                    <div className="text-sm text-gray-500 p-3">No notifications yet</div>
                                ) : (
                                    notifications.map((notification) => (
                                        <div
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            className="p-3 rounded-lg cursor-pointer transition-all duration-200 border bg-white border-gray-200 hover:bg-[#f8f9fa]"
                                        >
                                            <div className="flex items-start gap-2">
                                                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-[#275F97]"></div>

                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs leading-relaxed text-gray-900 font-medium">
                                                        {notification.notification_message}
                                                    </p>

                                                    <div className="flex items-center justify-between mt-2">
                                                        <span className="text-xs text-gray-500">
                                                            {new Date(notification.created_at).toLocaleDateString()}
                                                        </span>

                                                        {notification.overall_score && (
                                                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                                                                notification.overall_score >= 70
                                                                    ? 'bg-green-100 text-green-700'
                                                                    : notification.overall_score >= 40
                                                                        ? 'bg-yellow-100 text-yellow-700'
                                                                        : 'bg-red-100 text-red-700'
                                                            }`}>
                                                                {notification.overall_score}%
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer Logo */}
            <div className="bg-[#EAEFF5] px-5 py-4">
                <div className={`${isCollapsed ? 'w-8 h-8' : 'w-36 h-36'} flex items-center justify-center transition-all duration-300`}>
                    <a href="https://acespireconsulting.com/" target="_blank" rel="noreferrer">
                        <img
                            src="/assets/AcespireLogo.png"
                            alt="Active Creative Logo"
                            className="w-full h-full object-contain"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;